import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LxpLayout } from "@/components/lexpractiq/LxpLayout";
import { ClipboardList, Send } from "lucide-react";
import { listMyAssessments, getMyAssessment, submitMyAssessment } from "@/lib/api/lxp-assessments.functions";

export const Route = createFileRoute("/_authenticated/lexpractiq/my-assessments")({
  head: () => ({ meta: [{ title: "My Assessments | SA LexPractiq" }] }),
  component: MyAssessmentsPage,
});

function MyAssessmentsPage() {
  const list = useServerFn(listMyAssessments);
  const get = useServerFn(getMyAssessment);
  const submit = useServerFn(submitMyAssessment);

  const [items, setItems] = useState<any[]>([]);
  const [active, setActive] = useState<any | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});

  async function refresh() {
    try { const r: any = await list(); setItems(r.items); }
    catch (e: any) { toast.error(e?.message); }
  }
  useEffect(() => { refresh(); }, []);

  async function open(id: string) {
    try {
      const r: any = await get({ data: { assignmentId: id } });
      setActive(r.assignment); setAnswers({});
    } catch (e: any) { toast.error(e?.message); }
  }

  async function doSubmit() {
    if (!active) return;
    const questions: any[] = active.template.questions;
    const payload = questions.map((q) => ({ id: q.id, value: answers[q.id] ?? null }));
    try {
      const r: any = await submit({ data: { assignmentId: active.id, answers: payload } });
      toast.success(r.requiresManualGrading ? "Submitted — awaiting recruiter grading" : `Submitted — score ${r.autoScore}%`);
      setActive(null); refresh();
    } catch (e: any) { toast.error(e?.message); }
  }

  const q = active?.template?.questions ?? [];

  return (
    <LxpLayout>
      <div className="lxp-container" style={{ padding: "2rem 1.25rem" }}>
        <h1 style={{ margin: 0 }}><ClipboardList size={20} style={{ marginRight: 8 }} />My Assessments</h1>
        <p className="lxp-muted" style={{ margin: "0.3rem 0 1.5rem" }}>Assessments recruiters have sent to you. Complete them here and your results are saved to your profile.</p>

        {!active && (
          <div style={{ display: "grid", gap: "0.75rem" }}>
            {items.map((a) => (
              <div key={a.id} className="lxp-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
                <div>
                  <span className="lxp-badge">{a.status}</span>
                  <h3 style={{ margin: "0.3rem 0 0.15rem", fontSize: "0.95rem" }}>{a.template?.title}</h3>
                  <div className="lxp-muted" style={{ fontSize: "0.8rem" }}>
                    Sent {new Date(a.created_at).toLocaleDateString()}
                    {a.due_at ? ` · Due ${new Date(a.due_at).toLocaleDateString()}` : ""}
                    {a.score != null ? ` · Your score: ${a.score}%` : ""}
                  </div>
                  {a.feedback && <p style={{ margin: "0.4rem 0 0", fontSize: "0.85rem" }}>Feedback: {a.feedback}</p>}
                </div>
                <div>
                  {(a.status === "sent" || a.status === "in_progress") ? (
                    <button className="lxp-btn" onClick={() => open(a.id)}>Start</button>
                  ) : (
                    <span className="lxp-muted" style={{ fontSize: "0.8rem" }}>{a.status === "graded" ? "Graded" : "Submitted"}</span>
                  )}
                </div>
              </div>
            ))}
            {items.length === 0 && <p className="lxp-muted">No assessments assigned yet.</p>}
          </div>
        )}

        {active && (
          <div className="lxp-card">
            <h2 style={{ margin: "0 0 0.5rem" }}>{active.template.title}</h2>
            {active.template.description && <p className="lxp-muted">{active.template.description}</p>}
            {active.template.instructions && (
              <div className="lxp-card" style={{ marginBottom: "1rem", background: "var(--lxp-surface-alt, rgba(255,255,255,0.03))" }}>
                <strong>Instructions</strong>
                <p style={{ margin: "0.3rem 0 0", whiteSpace: "pre-wrap" }}>{active.template.instructions}</p>
              </div>
            )}
            {active.template.time_limit_min && <p className="lxp-muted" style={{ fontSize: "0.8rem" }}>Time limit: {active.template.time_limit_min} min · Passing: {active.template.passing_score}%</p>}

            {q.map((question: any, i: number) => (
              <div key={question.id} className="lxp-card" style={{ marginTop: "0.75rem" }}>
                <div className="lxp-muted" style={{ fontSize: "0.75rem" }}>Q{i + 1} · {question.points} pts</div>
                <p style={{ margin: "0.3rem 0 0.6rem", fontWeight: 600 }}>{question.prompt}</p>
                {question.type === "short" && (
                  <input className="lxp-input" value={answers[question.id] || ""} onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })} />
                )}
                {question.type === "long" && (
                  <textarea className="lxp-textarea" rows={5} value={answers[question.id] || ""} onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })} />
                )}
                {question.type === "mcq" && (
                  <div style={{ display: "grid", gap: "0.3rem" }}>
                    {(question.options || []).map((opt: string) => (
                      <label key={opt} style={{ display: "flex", gap: "0.5rem", alignItems: "center", cursor: "pointer" }}>
                        <input type="radio" name={question.id} checked={answers[question.id] === opt} onChange={() => setAnswers({ ...answers, [question.id]: opt })} />
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                )}
                {question.type === "boolean" && (
                  <div style={{ display: "flex", gap: "1rem" }}>
                    {["true", "false"].map((v) => (
                      <label key={v} style={{ display: "flex", gap: "0.4rem", alignItems: "center", cursor: "pointer" }}>
                        <input type="radio" name={question.id} checked={String(answers[question.id]) === v} onChange={() => setAnswers({ ...answers, [question.id]: v })} />
                        <span>{v === "true" ? "True" : "False"}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", justifyContent: "flex-end" }}>
              <button className="lxp-btn-ghost" onClick={() => setActive(null)}>Back</button>
              <button className="lxp-btn" onClick={doSubmit}><Send size={14} /> Submit</button>
            </div>
          </div>
        )}
      </div>
    </LxpLayout>
  );
}
